/**
 * A change order read back, months later, by the office.
 *
 * The sibling of `DocumentRecordModal`, and it exists for the same two
 * reasons. The customer's `/c/<token>` link refuses to open once it has been
 * signed — right for them, useless for us — and on one still live, opening it
 * as staff records a view no customer made, which is a small lie told to the
 * one record that exists to be trustworthy.
 *
 * So this reads the frozen snapshot straight off the row, under the signed-in
 * user's own row level security, and draws it with `ChangeOrderView`: the same
 * component the customer signed on, so what is on this screen cannot drift
 * from what they were looking at (ADR 0032). Lines withdrawn since come with
 * it, marked beside what was signed rather than in place of it (ADR 0181).
 *
 * Until 2026-09-23 there was no way into this at all: a signed change order
 * appeared in Docs nowhere, so the one document that changes a contract after
 * it is signed was the one document the office could not read back
 * (ADR 0205).
 */

import { formatMoney } from "@roof-hawk/core";

import { ErrorNote, Modal } from "../../components/ui";
import { t } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import { fetchChangeOrderRecord } from "../../lib/queries";
import { ChangeOrderView } from "../present/ChangeOrderPage";

export function ChangeOrderRecordModal({
  documentId,
  onClose,
}: {
  documentId: string;
  onClose: () => void;
}) {
  const record = useAsync(
    () => fetchChangeOrderRecord(documentId),
    [documentId],
  );
  const document = record.data?.document;
  const signature = record.data?.signature ?? null;

  return (
    <Modal
      title={
        document
          ? t("Change order {number}", { number: document.number })
          : t("Change order")
      }
      subtitle={
        signature
          ? (signature.signedDate
              ? t("Approved by {name} on {date}", {
                  name: signature.signerName,
                  date: signature.signedDate,
                })
              : t("Approved by {name}", { name: signature.signerName })) +
            (document ? ` · ${formatMoney(document.changeTotal)}` : "")
          : undefined
      }
      onClose={onClose}
      wide
    >
      <ErrorNote error={record.error} />
      {record.loading ? <p className="n">{t("Opening…")}</p> : null}
      {document ? (
        <ChangeOrderView
          document={document}
          signature={signature}
          readOnly
          withdrawn={record.data?.withdrawn ?? []}
          onSign={() => undefined}
        />
      ) : null}
    </Modal>
  );
}
